import { HexColor, type SessionContext, type Theme } from '@esign/contracts'

export type PartnerBranding = {
  partnerCode?: string
  partnerName?: string
  brandName?: string
  brandColor?: string
  /** Chỉ persist — không đưa vào Theme.logo_id (contract cấm URL ngoài). */
  logoUrl?: string | null
  sessionExpiredAt?: string
}

const storageKey = (sessionId: string): string => `esign_partner_branding_${sessionId}`

export function normalizeHexColor(value: string | null | undefined): string | undefined {
  if (!value) return undefined
  const cleaned = decodeURIComponent(value).trim().replace(/^%23/, '#').replace(/\s/g, '')
  const withHash = cleaned.startsWith('#') ? cleaned : `#${cleaned}`
  const parsed = HexColor.safeParse(withHash)
  return parsed.success ? parsed.data : undefined
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

/**
 * Lấy branding từ payload `test-session` (webview_config + partner_*).
 * Field lạ bị bỏ — không crash.
 */
export function brandingFromTestSession(payload: unknown): PartnerBranding {
  if (!payload || typeof payload !== 'object') return {}
  const data = payload as Record<string, unknown>
  const config =
    data['webview_config'] && typeof data['webview_config'] === 'object'
      ? (data['webview_config'] as Record<string, unknown>)
      : {}

  const brandColor =
    normalizeHexColor(readString(config['theme_color'])) ||
    normalizeHexColor(readString(config['theme']))

  return {
    partnerCode: readString(data['partner_code']),
    partnerName: readString(data['partner_name']),
    brandName: readString(config['brand_name']) || readString(data['partner_name']),
    brandColor,
    logoUrl: config['logo_url'] === null ? null : readString(config['logo_url']) ?? null,
    sessionExpiredAt: readString(data['session_expired_at']) || readString(data['expired_at']),
  }
}

export function persistPartnerBranding(sessionId: string, branding: PartnerBranding): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(storageKey(sessionId), JSON.stringify(branding))
}

export function loadPartnerBranding(sessionId: string): PartnerBranding | null {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(storageKey(sessionId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as PartnerBranding
    return {
      ...parsed,
      brandColor: normalizeHexColor(parsed.brandColor),
    }
  } catch {
    return null
  }
}

export function applyPartnerBranding(
  context: SessionContext,
  branding: PartnerBranding | null,
  urlTheme?: Partial<Pick<Theme, 'brand_color' | 'secondary_color' | 'on_primary'>>,
): SessionContext {
  const brandColor = urlTheme?.brand_color || branding?.brandColor
  const partnerName = branding?.brandName || branding?.partnerName
  const partnerId = branding?.partnerCode

  return {
    ...context,
    session: {
      ...context.session,
      ...(branding?.sessionExpiredAt ? { expires_at: branding.sessionExpiredAt } : {}),
    },
    partner: {
      ...context.partner,
      ...(partnerId ? { id: partnerId } : {}),
      ...(partnerName ? { name: partnerName } : {}),
    },
    theme: {
      ...context.theme,
      ...(brandColor ? { brand_color: brandColor } : {}),
      ...(urlTheme?.secondary_color ? { secondary_color: urlTheme.secondary_color } : {}),
      ...(urlTheme?.on_primary ? { on_primary: urlTheme.on_primary } : {}),
    },
  }
}
